import React from 'react';
import { Card, List, Typography, Button, Space, Tag, Popconfirm, Dropdown, Checkbox, Tooltip } from 'antd';
import { DeleteOutlined, HistoryOutlined, StarOutlined, StarFilled, PrinterOutlined, FilePdfOutlined, DownOutlined, InboxOutlined, LineChartOutlined } from '@ant-design/icons';
import './CalculationHistory.less';
import { getProtectionTypeName, formatResultLabel, formatResultValueExport, buildParamNotes } from '../utils/exportUtils';
import { calculateProtectionSettings } from '../utils/protectionCalculations';
import { useI18n } from '../i18n/i18n';

const { Title, Text } = Typography;

// 功能开关：历史汇总“导出 PDF”按钮
const ENABLE_HISTORY_EXPORT_PDF = true;

const MAX_NAME_LENGTH = 60;
const MAX_COMMENT_LENGTH = 300;
const MAX_TAG_LENGTH = 24;

const CalculationHistory = ({ history, loading, onDelete, onView, onRecalculate, onExport, onExportPdf, onPrintReport, onCopyJson, onUpdateMeta, onExportHtml, onExportWord, onShareLink, pagination, selectedIds = [], onSelectChange, onBulkExport, onBulkFavorite, onBulkArchive, onBulkTccOverlay, onBulkEditProject, onBulkEditLine }) => {
  const toggleFavorite = (item) => {
    const current = !!(item.result?.extras?.favorite);
    onUpdateMeta && onUpdateMeta(item.id, { extras: { favorite: !current } });
  };

  const tagsToString = (tags) => {
    if (Array.isArray(tags)) return tags.join(', ');
    if (typeof tags === 'string') return tags;
    return '';
  };

  const sanitizeName = (val) => (val || '').slice(0, MAX_NAME_LENGTH);
  const sanitizeComments = (val) => (val || '').slice(0, MAX_COMMENT_LENGTH);
  const sanitizeTagsString = (val) => {
    const arr = (val || '').split(',').map(s => s.trim()).filter(Boolean).map(s => s.slice(0, MAX_TAG_LENGTH));
    return arr;
  };

  const handleSelectItem = (id, checked) => {
    if (!onSelectChange) return;
    const set = new Set(selectedIds || []);
    if (checked) set.add(id); else set.delete(id);
    onSelectChange(Array.from(set));
  };

  const selectAllThisPage = () => {
    if (!onSelectChange) return;
    const ids = (history || []).map(i => i.id);
    const set = new Set([...(selectedIds || []), ...ids]);
    onSelectChange(Array.from(set));
  };

  const clearSelection = () => onSelectChange && onSelectChange([]);
  const { t } = useI18n();

  const buildSummaryText = (item) => {
    const type = item.calculation_type || item.parameters?.protectionType || 'unknown';
    let results = item.results;
    if (!results && item.parameters) {
      try {
        results = calculateProtectionSettings(item.parameters);
      } catch (_) {
        results = {};
      }
    }
    const entries = Object.entries(results || {})
      .filter(([k, v]) => k !== 'curvePoints' && v !== undefined && v !== null)
      .slice(0, 4);
    if (entries.length === 0) return '';
    return entries.map(([k, v]) => `${formatResultLabel(k, type)}: ${formatResultValueExport(k, v)}`).join('， ');
  };

  const buildParamNotesText = (item) => {
    const extras = item?.result?.extras || {};
    if (extras.includeParamNotes === false) return '';
    const type = item.calculation_type || item.parameters?.protectionType || 'unknown';
    const pairs = buildParamNotes(item.parameters || {}, type) || [];
    if (!pairs || pairs.length === 0) return '';
    return pairs.map(([label, text]) => `${label}: ${text}`).join('； ');
  };

  const buildDeviceSourceText = (item) => {
    const extras = item?.result?.extras || {};
    if (extras.includeDeviceSource === false) return '';
    const d = extras.deviceDetails || {};
    const r = extras.deviceRefs || {};
    // 如果有详细信息，优先展示详细信息
    const parts = [];
    if (d && Object.keys(d).length > 0) {
      if (d.feeder?.name || d.feeder?.id) {
        const name = d.feeder?.name || '';
        const id = d.feeder?.id ? `(${d.feeder.id})` : '';
        parts.push(`馈线：${[name, id].filter(Boolean).join('')}`);
      }
      if (d.bus && (d.bus.voltage_kV || d.bus.name || d.bus.id)) {
        const v = d.bus?.voltage_kV != null ? `${d.bus.voltage_kV} kV` : '';
        const name = d.bus?.name || '';
        const id = d.bus?.id ? `(${d.bus.id})` : '';
        parts.push(`母线：${[name, id, v].filter(Boolean).join(' ')}`);
      }
      if (d.rated_current_A != null) parts.push(`额定电流：${d.rated_current_A} A`);
      if (d.ct && (d.ct.ratio || d.ct.id)) {
        const ratio = d.ct?.ratio != null ? `1:${d.ct.ratio}` : '';
        const id = d.ct?.id ? `(${d.ct.id})` : '';
        parts.push(`CT：${[ratio, id].filter(Boolean).join(' ')}`);
      }
      if (d.pt && (d.pt.ratio || d.pt.id)) {
        const ratio = d.pt?.ratio != null ? `${d.pt.ratio}` : '';
        const id = d.pt?.id ? `(${d.pt.id})` : '';
        parts.push(`PT：${[ratio, id].filter(Boolean).join(' ')}`);
      }
      if (d.source && (d.source.short_circuit_MVA || d.source.X_R)) {
        const sc = d.source?.short_circuit_MVA != null ? `${d.source.short_circuit_MVA} MVA` : '';
        const xr = d.source?.X_R != null ? `X/R=${d.source.X_R}` : '';
        parts.push(`电源短路容量：${[sc, xr].filter(Boolean).join('； ')}`);
      }
      if (d.transformer && (d.transformer.S_MVA || d.transformer.uk_percent || d.transformer.vector_group)) {
        const s = d.transformer?.S_MVA != null ? `${d.transformer.S_MVA} MVA` : '';
        const uk = d.transformer?.uk_percent != null ? `uk=${d.transformer.uk_percent}%` : '';
        const vg = d.transformer?.vector_group ? `组别=${d.transformer.vector_group}` : '';
        const ng = d.transformer?.secondary_neutral_ground != null ? `中性点接地=${d.transformer.secondary_neutral_ground ? '是' : '否'}` : '';
        parts.push(`变压器：${[s, uk, vg, ng].filter(Boolean).join('； ')}`);
      }
      if (d.line && (d.line.length_km || d.line.R_per_km_ohm || d.line.X_per_km_ohm)) {
        const len = d.line?.length_km != null ? `长度=${d.line.length_km} km` : '';
        const rkm = d.line?.R_per_km_ohm != null ? `R=${d.line.R_per_km_ohm} Ω/km` : '';
        const xkm = d.line?.X_per_km_ohm != null ? `X=${d.line.X_per_km_ohm} Ω/km` : '';
        const z0 = d.line?.z0_factor != null ? `零序系数=${d.line.z0_factor}` : '';
        parts.push(`线路：${[len, rkm, xkm, z0].filter(Boolean).join('； ')}`);
      }
      if (d.grounding && (d.grounding.R_ohm != null || d.grounding.X_ohm != null)) {
        const gr = d.grounding?.R_ohm != null ? `R=${d.grounding.R_ohm} Ω` : '';
        const gx = d.grounding?.X_ohm != null ? `X=${d.grounding.X_ohm} Ω` : '';
        parts.push(`接地阻抗：${[gr, gx].filter(Boolean).join('； ')}`);
      }
      return parts.join('； ');
    }
    // 否则退回引用 ID 信息
    const refParts = [];
    if (r.feeder_id) refParts.push(`馈线ID：${r.feeder_id}`);
    if (r.ct_id) refParts.push(`CT ID：${r.ct_id}`);
    if (r.pt_id) refParts.push(`PT ID：${r.pt_id}`);
    if (r.relay_id) refParts.push(`继电器ID：${r.relay_id}`);
    return refParts.join('； ');
  };

  return (
    <Card className="calculation-history-card">
      <div className="history-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <HistoryOutlined />
          <Title level={4} style={{ margin: 0 }}>计算历史</Title>
        </Space>
        <Space>
          <Text type="secondary">{history.length} 条记录</Text>
          <Text type="secondary">已选 {selectedIds?.length || 0} 项</Text>
          <Button size="small" onClick={selectAllThisPage}>本页全选</Button>
          <Button size="small" onClick={clearSelection}>清空选择</Button>
          <Button size="small" type="primary" disabled={!selectedIds || selectedIds.length === 0} onClick={() => onBulkExport && onBulkExport(selectedIds)}>批量导出</Button>
          <Button size="small" disabled={!selectedIds || selectedIds.length === 0} onClick={() => onBulkFavorite && onBulkFavorite(selectedIds)}>批量收藏</Button>
          <Button size="small" disabled={!selectedIds || selectedIds.length === 0} onClick={() => onBulkArchive && onBulkArchive(selectedIds)}>批量归档</Button>
          <Button size="small" type="dashed" icon={<LineChartOutlined />} disabled={!selectedIds || selectedIds.length < 2} onClick={() => onBulkTccOverlay && onBulkTccOverlay(selectedIds)}>批量叠加 TCC</Button>
          <Button size="small" disabled={!selectedIds || selectedIds.length === 0} onClick={() => {
            if (!onBulkEditProject) return;
            const val = window.prompt('输入新的项目名称：', '');
            if (val != null) onBulkEditProject(selectedIds, val);
          }}>批量编辑项目</Button>
          <Button size="small" disabled={!selectedIds || selectedIds.length === 0} onClick={() => {
            if (!onBulkEditLine) return;
            const val = window.prompt('输入新的线路名称：', '');
            if (val != null) onBulkEditLine(selectedIds, val);
          }}>批量编辑线路</Button>
        </Space>
      </div>
      
      <List
        loading={loading}
        dataSource={history}
        locale={{ emptyText: '暂无计算历史记录' }}
        pagination={pagination}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            actions={[
              <Button key="view" type="link" size="small" onClick={() => onView && onView(item)}>
                查看
              </Button>,
              <Button key="recalc" type="link" size="small" onClick={() => onRecalculate && onRecalculate(item)}>
                重新计算
              </Button>,
              <Button key="copyJson" type="link" size="small" onClick={() => onCopyJson && onCopyJson(item)}>
                复制 JSON
              </Button>,
              <Dropdown
                key="exportDropdown"
                trigger={["click"]}
                placement="bottomLeft"
                menu={{
                  items: [
                    { key: 'md', label: '导出 Markdown', onClick: () => onExport && onExport(item) },
                    { key: 'html', label: '导出 HTML', onClick: () => onExportHtml && onExportHtml(item) },
                    { key: 'word', label: '导出 Word', onClick: () => onExportWord && onExportWord(item) },
                    ...(ENABLE_HISTORY_EXPORT_PDF ? [{ key: 'pdf', label: '导出 PDF', icon: <FilePdfOutlined />, onClick: () => onExportPdf && onExportPdf(item) }] : [])
                  ]
                }}
              >
                <Button type="link" size="small">
                  <Space size={4}>
                    导出
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>,
              <Button key="print" type="link" size="small" icon={<PrinterOutlined />} onClick={() => onPrintReport && onPrintReport(item)}>
                打印视图
              </Button>,
              <Button key="share" type="link" size="small" onClick={() => onShareLink && onShareLink(item)}>
                分享链接
              </Button>,
              <Popconfirm
                key="delete"
                title="确定要删除这条记录吗？"
                onConfirm={() => onDelete(item.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="text" icon={<DeleteOutlined />} danger size="small">
                  删除
                </Button>
              </Popconfirm>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Checkbox
                  checked={selectedIds?.includes(item.id)}
                  onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                />
              }
              title={
                <Space wrap>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => toggleFavorite(item)}
                    icon={item.result?.extras?.favorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                    style={{ padding: 0 }}
                    title={item.result?.extras?.favorite ? '取消收藏' : '收藏置顶'}
                  />
                  <Tag color="blue">{getProtectionTypeName(item.calculation_type || item.parameters?.protectionType, { t })}</Tag>
                  {(function(){
                    const extras = item?.result?.extras || {};
                    const hasTuning = Array.isArray(extras.autoTuningLogEvents) && extras.autoTuningLogEvents.length > 0;
                    if (!hasTuning) return null;
                    return (
                      <Tooltip title="由自动整定向导应用建议，记录了参数变更与理由">
                        <Tag color="green">自动整定</Tag>
                      </Tooltip>
                    );
                  })()}
                  {item.result?.extras?.archived ? (
                    <Tooltip title="已归档">
                      <InboxOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  ) : null}
                  <Text
                    strong
                    type={item.result?.extras?.archived ? 'secondary' : undefined}
                    ellipsis={{ tooltip: item.result?.name || '' }}
                    editable={{ onChange: (val) => onUpdateMeta && onUpdateMeta(item.id, { name: sanitizeName(val) }) }}
                  >{sanitizeName(item.result?.name || '')}</Text>
                </Space>
              }
              description={
                <div>
                  {/* 项目 */}
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">项目：</Text>
                    <Text
                      editable={{ onChange: (val) => onUpdateMeta && onUpdateMeta(item.id, { extras: { project: val } }) }}
                    >{item.result?.extras?.project || ''}</Text>
                  </div>
                  {/* 线路 */}
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">线路：</Text>
                    <Text
                      editable={{ onChange: (val) => onUpdateMeta && onUpdateMeta(item.id, { extras: { line: val } }) }}
                    >{item.result?.extras?.line || ''}</Text>
                  </div>
                  {/* 标签与批注合并同一行，批注为单行内联可编辑 */}
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center' }}>
                    <Text type="secondary">标签：</Text>
                    <Text
                      style={{ marginRight: 8 }}
                      ellipsis={{ tooltip: tagsToString(item.result?.extras?.tags) }}
                      editable={{ onChange: (val) => onUpdateMeta && onUpdateMeta(item.id, { extras: { tags: sanitizeTagsString(val) } }) }}
                    >{tagsToString(item.result?.extras?.tags)}</Text>
                    <Space size={4} style={{ marginRight: 12 }}>
                      {(item.result?.extras?.tags || []).map((t, idx) => (
                        <Tooltip key={idx} title={t}>
                          <Tag color="gold">{(t || '').slice(0, MAX_TAG_LENGTH)}</Tag>
                        </Tooltip>
                      ))}
                    </Space>
                    <Text type="secondary" style={{ marginRight: 4 }}>批注：</Text>
                    <Text
                      style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
                      editable={{ onChange: (val) => onUpdateMeta && onUpdateMeta(item.id, { extras: { comments: sanitizeComments(val) } }) }}
                      ellipsis={{ tooltip: item.result?.extras?.comments || '' }}
                    >{sanitizeComments(item.result?.extras?.comments || '')}</Text>
                  </div>
                  {/* 结果摘要（统一共享格式化） */}
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">摘要：</Text>
                    <Text>{buildSummaryText(item)}</Text>
                  </div>
                  {/* 自动整定摘要与事件概览（来自 extras） */}
                  {(function(){
                    const extras = item?.result?.extras || {};
                    const tuningSummary = extras?.autoTuningParamSummary;
                    const tuningEvents = Array.isArray(extras?.autoTuningLogEvents) ? extras.autoTuningLogEvents : [];
                    if (!tuningSummary && tuningEvents.length === 0) return null;
                    const lastActionText = tuningSummary?.lastAction === 'apply' ? '应用建议' : (tuningSummary?.lastAction === 'rollback' ? '回滚至快照' : (tuningSummary?.lastAction || ''));
                    let lastTimeText = '';
                    try { lastTimeText = tuningSummary?.lastTime ? new Date(tuningSummary.lastTime).toLocaleString('zh-CN') : ''; } catch(_) {}
                    return (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">自动整定：</Text>
                        <Text>
                          {typeof tuningSummary?.applyCount === 'number' ? `应用${tuningSummary.applyCount}次` : ''}
                          {typeof tuningSummary?.rollbackCount === 'number' ? `${tuningSummary?.applyCount ? '；' : ''}回滚${tuningSummary.rollbackCount}次` : ''}
                          {lastActionText ? `${(tuningSummary?.applyCount || tuningSummary?.rollbackCount) ? '；' : ''}最近：${lastActionText}${lastTimeText ? `（${lastTimeText}）` : ''}` : ''}
                          {tuningEvents.length > 0 ? `${(tuningSummary ? '；' : '')}事件数：${tuningEvents.length}` : ''}
                        </Text>
                      </div>
                    );
                  })()}
                  {/* 参数说明（与报告模板一致） */}
                  {(function(){
                    const text = buildParamNotesText(item);
                    if (!text) return null;
                    return (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">参数说明：</Text>
                        <Text>{text}</Text>
                      </div>
                    );
                  })()}
                  {/* 设备来源（与报告模板一致） */}
                  {(function(){
                    const text = buildDeviceSourceText(item);
                    if (!text) return null;
                    return (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">设备来源：</Text>
                        <Text>{text}</Text>
                      </div>
                    );
                  })()}
                  {/* 叠加排序与选中顺序（若可用） */}
                  {(function(){
                    const extras = item?.result?.extras || {};
                    const selectivityPanel = extras?.selectivityPanel;
                    const overlay = extras?.overlay;
                    const panelEnabled = !!(selectivityPanel && selectivityPanel.enabled && Array.isArray(selectivityPanel.seriesOrder) && selectivityPanel.seriesOrder.length > 1);
                    if (!overlay && !panelEnabled) return null;
                    const sortText = panelEnabled
                      ? `${t('export.overlay.sort.by_panel_selection_prefix')}（${(selectivityPanel.seriesOrder || []).join(' → ')}）`
                      : t('export.overlay.sort.by_current_view');
                    return (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">{t('overlay.md.sort_mode_prefix')}</Text>
                        <Text>{sortText}</Text>
                        {panelEnabled ? (
                          <div>
                            <Text type="secondary">{t('overlay.md.selection_order_prefix')}</Text>
                            <Text>{(selectivityPanel.seriesOrder || []).join(' → ')}</Text>
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

export default CalculationHistory;
